import { AlertCircle, Clock, UserCircle2 } from 'lucide-react';
import { formatDateTime } from '../../lib/dateFmt';
import { useAssetHistory, type AssetHistoryEvent } from '../../hooks/useAssets';

/**
 * Lifecycle timeline for one asset: every KitIssued / KitReturned /
 * KitFormSubmitted event that references it, newest work last. Split out
 * of AssetForm to keep that file under the size cap.
 */
export function AssetHistoryPanel({ assetId }: { assetId: string }) {
  const history = useAssetHistory(assetId);
  return (
    <section className="mt-8">
      <div className="flex items-baseline gap-2">
        <Clock className="h-4 w-4 text-fg-muted" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-fg-muted">History</h2>
        {history.data ? (
          <span className="text-xs text-fg-dim">
            {history.data.length} event{history.data.length === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>

      {history.isLoading ? (
        <div className="mt-3 text-sm text-fg-muted">Loading history…</div>
      ) : history.error ? (
        <div className="mt-3 flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{(history.error as Error).message}</span>
        </div>
      ) : !history.data || history.data.length === 0 ? (
        <div className="mt-3 rounded-md border border-dashed border-line-strong/20 px-4 py-6 text-center text-sm text-fg-muted">
          No events reference this asset yet. The first KitIssued event will appear here.
        </div>
      ) : (
        <ol className="mt-3 space-y-2">
          {history.data.map((ev) => (
            <HistoryRow key={ev.id} ev={ev} />
          ))}
        </ol>
      )}
    </section>
  );
}

function HistoryRow({ ev }: { ev: AssetHistoryEvent }) {
  const isIssue = ev.event_type === 'KitIssued';
  const isReturn = ev.event_type === 'KitReturned';
  const isHandover = ev.event_type === 'KitFormSubmitted';
  // Confirm-on-arrival: a KitIssued whose item was confirmed reads as
  // "Confirmed on arrival" rather than "Issued".
  const confirmed = isIssue && ev.item?.outcome === 'confirmed';
  const label = confirmed
    ? 'Confirmed on arrival'
    : isIssue
      ? 'Issued'
      : isReturn
        ? 'Returned'
        : isHandover
          ? 'Handover signed'
          : ev.event_type;
  const tone = isIssue
    ? 'bg-success-soft/15 text-success-soft-fg ring-success-soft/25'
    : isReturn
      ? 'bg-warning-soft/15 text-warning-soft-fg ring-warning-soft/25'
      : isHandover
        ? 'bg-info-soft/15 text-info-soft-fg ring-info-soft/25'
        : 'bg-surface-2 text-fg-muted ring-line/15';
  const handoverSubmissionId = isHandover ? (ev.submission_id ?? null) : null;
  const when = new Date(ev.created_at);
  return (
    <li className={`flex flex-wrap items-center gap-3 rounded-md p-3 ring-1 ${tone}`}>
      <span className="inline-flex items-center gap-1 rounded-full bg-surface px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ring-1 ring-line/15">
        {label}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm">
          <span className="font-medium">{ev.person.full_name ?? 'Unknown person'}</span>
          {ev.item?.size ? (
            <span className="text-xs text-fg-muted">size {ev.item.size}</span>
          ) : null}
          {ev.item?.serial ? (
            <span className="font-mono text-[11px] text-fg-muted">SN {ev.item.serial}</span>
          ) : null}
          {ev.item?.registration ? (
            <span className="font-mono text-[11px] text-fg-muted">{ev.item.registration}</span>
          ) : null}
          {ev.item?.last_service_date ? (
            <span className="text-[11px] text-fg-muted">last serviced {ev.item.last_service_date}</span>
          ) : null}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-fg-muted">
          <span title={when.toISOString()}>{formatDateTime(when)}</span>
          {ev.actor ? (
            <span className="inline-flex items-center gap-1">
              <UserCircle2 className="h-3 w-3" />
              {ev.actor.display_name ?? 'unknown actor'}
            </span>
          ) : (
            <span className="italic text-fg-dim">no actor recorded</span>
          )}
          {ev.notes ? (
            <span className="truncate" title={ev.notes}>
              {ev.notes}
            </span>
          ) : null}
          {handoverSubmissionId ? (
            <a
              href={`/api/kit-handover/submissions/${handoverSubmissionId}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-accent hover:underline"
            >
              View checklist PDF
            </a>
          ) : null}
        </div>
      </div>
    </li>
  );
}
